import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Calendar, Clock, User, ShieldCheck, Lock, Headphones, Heart, Sparkles, ChevronDown } from 'lucide-react';
import { SearchFilterState } from '../types';
import { DynamicLocationSearchBox } from './location/DynamicLocationSearchBox';
import { StructuredLocation, SearchRadiusKm } from '../types/location';
import { getCurrentDateString, getCurrentTimeString } from '../utils/dateUtils';
import { useLanguage } from '../i18n/LanguageContext';

interface HeroSectionProps {
  filters: SearchFilterState;
  setFilters: React.Dispatch<React.SetStateAction<SearchFilterState>>;
  onSearch: () => void;
  onSelectGenderQuick: (gender: 'male' | 'female') => void;
  onOpenSafety: () => void;
  onSelectService?: (serviceName: string) => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  filters,
  setFilters,
  onSearch,
  onSelectGenderQuick,
  onOpenSafety,
  onSelectService,
}) => {
  const { t } = useLanguage();
  const [selectedGender, setSelectedGender] = useState<string>(filters.gender);
  const [selectedCity, setSelectedCity] = useState<string>(filters.city);
  const [selectedLocation, setSelectedLocation] = useState<StructuredLocation | null>(filters.location || null);
  const [selectedRadius, setSelectedRadius] = useState<SearchRadiusKm>(filters.radiusKm || 25);
  const [selectedDate, setSelectedDate] = useState<string>(filters.date || getCurrentDateString());
  const [selectedTime, setSelectedTime] = useState<string>(filters.time || getCurrentTimeString());

  useEffect(() => {
    setSelectedGender(filters.gender);
    setSelectedCity(filters.city);
    setSelectedLocation(filters.location || null);
    if (filters.radiusKm) setSelectedRadius(filters.radiusKm);
    if (filters.date) setSelectedDate(filters.date);
    if (filters.time) setSelectedTime(filters.time);
  }, [filters]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      gender: selectedGender,
      city: selectedCity,
      location: selectedLocation,
      radiusKm: selectedRadius,
      date: selectedDate,
      time: selectedTime,
    }));
    onSearch();
  };

  return (
    <section id="hero-section" className="relative overflow-hidden pt-4 pb-12 lg:pt-8 lg:pb-16 bg-gradient-to-b from-pink-50/50 via-white to-pink-50/20">
      {/* Decorative floating ambient hearts */}
      <div className="absolute top-10 left-[8%] text-pink-300/40 text-4xl animate-float-slow pointer-events-none select-none">
        💕
      </div>
      <div className="absolute top-36 right-[12%] text-pink-400/30 text-5xl animate-float-gentle pointer-events-none select-none">
        💖
      </div>
      <div className="absolute top-72 left-[5%] text-purple-300/30 text-3xl animate-float-slow pointer-events-none select-none">
        ✨
      </div>
      <div className="absolute bottom-20 right-[6%] text-pink-300/30 text-4xl animate-float-gentle pointer-events-none select-none">
        💗
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-4 items-center">
          
          {/* Left Column: Headline & Call To Actions */}
          <div className="lg:col-span-7 space-y-6 z-10">
            {/* Promotional Earning Announcement Banner (Requirement 7) */}
            <Link
              to="/companion/register"
              className="group block relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-900 via-indigo-900 to-pink-900 p-[1.5px] shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.01] cursor-pointer max-w-lg"
              aria-label="Become a Companion, Earn Up to ₹1 Lakh. Join PairUpToday and start your journey."
            >
              <div className="relative rounded-[15px] bg-gradient-to-r from-purple-950/95 via-slate-900/95 to-pink-950/95 px-4 py-3 sm:px-5 sm:py-3.5 backdrop-blur-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-400/30 text-[10px] sm:text-xs font-black tracking-wider uppercase">
                      <Sparkles className="w-3 h-3 text-purple-400 animate-pulse" />
                      <span>{t('cta.becomeCompanion', 'BECOME A COMPANION')}</span>
                    </span>
                    <span className="text-pink-400 font-black text-xs sm:text-sm tracking-tight flex items-center gap-1">
                      <span>{t('cta.earnAmount', 'EARN UP TO ₹1 LAKH')}</span>
                      <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
                    </span>
                  </div>
                  <p className="text-[11px] sm:text-xs text-slate-300 font-medium">
                    {t('cta.supportingText', 'Join PairUpToday and start your journey.')}
                  </p>
                </div>
                <div className="shrink-0 flex items-center">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-extrabold text-xs shadow-md group-hover:from-pink-600 group-hover:to-rose-700 transition-all">
                    <span>{t('cta.applyNow', 'Apply Now')}</span>
                    <span className="group-hover:translate-x-0.5 transition-transform">→</span>
                  </span>
                </div>
              </div>
            </Link>

            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.12]">
                Find Your Perfect <span className="text-pink-600 inline-block">Companion</span>
                <br />
                or <span className="text-purple-600 inline-block">Partner</span>
                <br />
                for Any Occasion <span className="text-pink-500 inline-block animate-pulse">💕</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 max-w-xl font-normal leading-relaxed">
                {t('hero.subtitle')}
              </p>
            </div>

            {/* Direct category quick buttons */}
            <div className="flex flex-wrap gap-3.5 pt-1">
              <button
                id="btn-find-boyfriend"
                onClick={() => onSelectGenderQuick('male')}
                className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold text-sm rounded-full shadow-md shadow-pink-500/25 hover:shadow-pink-500/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
              >
                <Search className="w-4 h-4" />
                {t('hero.findMale')}
              </button>

              <button
                id="btn-find-girlfriend"
                onClick={() => onSelectGenderQuick('female')}
                className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-sm rounded-full shadow-md shadow-purple-600/25 hover:shadow-purple-600/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
              >
                <Search className="w-4 h-4" />
                {t('hero.findFemale')}
              </button>
            </div>
          </div>

          {/* Right Column: Hero Couple Visual Image matching screenshot */}
          <div className="lg:col-span-5 relative flex justify-center items-center">
            {/* Background radiant aura */}
            <div className="absolute inset-0 bg-gradient-to-tr from-pink-200/60 via-purple-200/40 to-rose-100/60 rounded-full filter blur-3xl -z-10 transform scale-110" />
            
            <div className="relative w-full max-w-md">
              {/* Couple visual card */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/80 bg-gradient-to-br from-pink-100/90 via-purple-50/70 to-rose-100/90">
                <img
                  src="https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&w=1000&q=85"
                  alt="Beautiful Indian Bengali companion"
                  className="w-full h-[360px] sm:h-[400px] object-cover object-top hover:scale-102 transition-transform duration-500"
                  referrerPolicy="no-referrer"
                />

                {/* Floating badge on top right */}
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-full shadow-md border border-pink-100 flex items-center gap-1.5 animate-float-gentle">
                  <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-ping" />
                  <span className="text-xs font-bold text-slate-800">500+ Active Companions</span>
                </div>

                {/* Floating badge on bottom left */}
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-md p-3 rounded-2xl shadow-lg border border-pink-100 max-w-[210px]">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold text-sm">
                      ✨
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900 leading-tight">Instant Match</p>
                      <p className="text-[10px] text-slate-500">Safe, verified & polite</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Interactive Search Bar matching image */}
        <div className="mt-8 lg:mt-10 relative z-20">
          <form
            id="hero-search-form"
            onSubmit={handleSearchSubmit}
            className="bg-white rounded-2xl sm:rounded-3xl shadow-xl shadow-slate-200/70 border border-slate-100 p-3 sm:p-4 md:p-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4 items-center">
              
              {/* Field 1: I am looking for */}
              <div className="lg:col-span-3 bg-slate-50/80 hover:bg-slate-50 border border-slate-200/70 rounded-xl sm:rounded-2xl p-2.5 sm:p-3 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-pink-500" />
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    I am looking for
                  </label>
                </div>
                <div className="relative">
                  <select
                    id="search-gender-select"
                    value={selectedGender}
                    onChange={(e) => setSelectedGender(e.target.value)}
                    className="w-full bg-transparent font-bold text-sm text-slate-800 focus:outline-none appearance-none cursor-pointer pr-6"
                  >
                    <option value="all">Any Companion</option>
                    <option value="female">Female Companion</option>
                    <option value="male">Male Companion</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-400 absolute right-0 top-0.5 pointer-events-none" />
                </div>
              </div>

              {/* Field 2: Google Places Dynamic Location Search */}
              <div className="lg:col-span-3 bg-slate-50/80 hover:bg-slate-50 border border-slate-200/70 rounded-xl sm:rounded-2xl p-2.5 sm:p-3 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-pink-500" />
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center justify-between w-full">
                    <span>Discovery Location</span>
                    <span className="text-[10px] text-pink-600 font-bold lowercase">Google Places (IN)</span>
                  </label>
                </div>
                <div>
                  <DynamicLocationSearchBox
                    placeholder="📍 Search your city, area or location"
                    selectedLocation={selectedLocation}
                    onLocationSelect={(loc) => {
                      setSelectedLocation(loc);
                      if (loc) {
                        setSelectedCity(loc.city || loc.formatted_address);
                      } else {
                        setSelectedCity('all');
                      }
                    }}
                    size="sm"
                  />
                </div>
              </div>

              {/* Field 3: Date */}
              <div className="lg:col-span-2 bg-slate-50/80 hover:bg-slate-50 border border-slate-200/70 rounded-xl sm:rounded-2xl p-2.5 sm:p-3 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-pink-500" />
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Date
                  </label>
                </div>
                <input
                  id="search-date-input"
                  type="date"
                  min={getCurrentDateString()}
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full bg-transparent font-bold text-sm text-slate-800 focus:outline-none cursor-pointer"
                />
              </div>

              {/* Field 4: Time */}
              <div className="lg:col-span-2 bg-slate-50/80 hover:bg-slate-50 border border-slate-200/70 rounded-xl sm:rounded-2xl p-2.5 sm:p-3 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-pink-500" />
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Time
                  </label>
                </div>
                <input
                  id="search-time-input"
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full bg-transparent font-bold text-sm text-slate-800 focus:outline-none cursor-pointer"
                />
              </div>

              {/* Action Button: Search Companion */}
              <div className="lg:col-span-2">
                <button
                  id="btn-search-companion"
                  type="submit"
                  className="w-full h-full min-h-[52px] flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-pink-600 via-rose-500 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-bold text-sm rounded-xl sm:rounded-2xl shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                >
                  <Search className="w-4 h-4" />
                  <span>Search Companion</span>
                </button>
              </div>

            </div>
          </form>

          {/* Quick Popular Services shortcuts */}
          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none text-xs">
            <span className="font-bold text-slate-500 shrink-0">Popular Services:</span>
            {['Movies', 'Shopping', 'Travel', 'Medical Help', 'Elder Care', 'Dinner', 'Tutoring', 'Culture', 'Parties'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  if (onSelectService) {
                    onSelectService(s);
                  } else {
                    setFilters(prev => ({ ...prev, service: s }));
                  }
                  const el = document.getElementById('choose-a-service-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-3 py-1 rounded-full bg-white hover:bg-pink-50 border border-slate-200 hover:border-pink-300 text-slate-700 hover:text-pink-600 font-semibold shrink-0 transition-colors shadow-2xs cursor-pointer"
              >
                {s}
              </button>
            ))}
          </div>

          {/* Trust Badges matching screenshot */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
            
            <div
              id="trust-badge-verified"
              onClick={onOpenSafety}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-pink-50/60 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h4 className="text-xs font-bold text-slate-900">100% Verified</h4>
                <p className="text-[11px] text-slate-500">Profiles Checked</p>
              </div>
            </div>

            <div
              id="trust-badge-security"
              onClick={onOpenSafety}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-pink-50/60 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h4 className="text-xs font-bold text-slate-900">Safe & Secure</h4>
                <p className="text-[11px] text-slate-500">Protected Platform</p>
              </div>
            </div>

            <div
              id="trust-badge-support"
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-pink-50/60 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <Headphones className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h4 className="text-xs font-bold text-slate-900">24/7 Customer</h4>
                <p className="text-[11px] text-slate-500">Support Center</p>
              </div>
            </div>

            <div
              id="trust-badge-privacy"
              onClick={onOpenSafety}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-pink-50/60 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <User className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h4 className="text-xs font-bold text-slate-900">Privacy</h4>
                <p className="text-[11px] text-slate-500">First Experience</p>
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
